import type { FunderSearchSource } from "@lib/funderSearchSources";

/**
 * Shared types and constants for funder workspace search.
 *
 * This module is imported by the main thread (hook + UI), the search worker,
 * and the sign-out flow, so it must stay dependency-free: types, plain
 * constants, and nothing that drags in React, Dexie, or MiniSearch.
 */

export type SearchDocumentType =
  | "transcript-chunk"
  | "proposal-summary"
  | "track-summary"
  | "round-summary"
  | "recommender-summary"
  | "proposal"
  | "proposal-note";

export type SearchDocument = {
  id: string;
  type: SearchDocumentType;
  title: string;
  text: string;
  /**
   * Content fingerprint used to diff extracted documents against the indexed
   * ones — when it changes the document is re-indexed (MiniSearch `replace`).
   */
  version: string;
  proposalId?: string;
  memberId?: string;
  recommenderId?: string;
  /** AssignableTrack value, for navigation to the track page. */
  track?: string;
  transcriptId?: string;
  /**
   * For transcript chunks: the chunk's `[start, end)` character range in the
   * source transcript content, computed at index time so deep links can
   * anchor to the clicked chunk without re-deriving the chunking.
   */
  chunkStart?: number;
  chunkEnd?: number;
};

/**
 * Fields persisted inside the MiniSearch index itself, so search results are
 * renderable and navigable even before the corpus (and therefore the
 * main-thread document map) has loaded — e.g. right after an offline reload
 * where the index was restored from IndexedDB.
 */
export type SearchStoredFields = {
  type: SearchDocumentType;
  title: string;
  proposalId?: string;
  recommenderId?: string;
  track?: string;
  transcriptId?: string;
  chunkStart?: number;
  chunkEnd?: number;
};

export type SearchWorkerHit = SearchStoredFields & {
  id: string;
  score: number;
  /** Document terms the query matched, used to build snippets. */
  terms: string[];
  snippet: string | null;
  firstMatchedTerm: string | null;
};

export type SearchWorkerRequest =
  | {
      type: "init";
      roundId: string;
      /**
       * Effective (impersonated) user id. Part of the persisted-index cache
       * key so an admin's index is never restored into an impersonated
       * session, whose own queries may be denied content the admin could see.
       */
      cacheScope: string;
    }
  | {
      type: "apply-source-delta";
      sources: FunderSearchSource[];
      removedSourceIds: string[];
      replaceAll: boolean;
    }
  | { type: "search"; requestId: number; query: string };

export type SearchWorkerResponse =
  | {
      type: "status";
      cause: "init" | "apply-source-delta";
      indexedCount: number;
      restoredFromCache: boolean;
      /**
       * Size of the serialized index as last persisted/restored, for
       * storage diagnostics. Null until the first persist of a fresh index.
       */
      indexBytes: number | null;
      /** Worker time spent on this step (restore / document apply), in ms. */
      tookMs: number;
      /** Document changes applied, present for source-delta acknowledgements. */
      changes?: { added: number; replaced: number; discarded: number };
      sourceVersions?: Record<string, string>;
      needsSourceHydration?: boolean;
    }
  | {
      type: "search-results";
      requestId: number;
      hits: SearchWorkerHit[];
      /** Total matches before truncation to SEARCH_RESULT_LIMIT. */
      totalHits: number;
      /** Time MiniSearch spent on this query, in milliseconds. */
      tookMs: number;
    }
  | { type: "error"; message: string };

/** Fully-resolved result handed to the UI by `useFunderSearch`. */
export type SearchResultItem = SearchStoredFields & {
  id: string;
  score: number;
  snippet: string | null;
  /** Document terms the query matched, for highlighting. */
  terms: string[];
  /** First document-side matched term, preserving the document's casing. */
  firstMatchedTerm: string | null;
};

export type SearchResults = {
  items: SearchResultItem[];
  /** Total matches; greater than `items.length` when truncated. */
  totalHits: number;
};

/** Dexie database holding the persisted MiniSearch index (one row per round). */
export const SEARCH_INDEX_DB_NAME = "FunderWorkspaceSearch";

export const SEARCH_RESULT_LIMIT = 50;

/** Paragraph-window chunking targets for transcript text, in characters. */
export const TRANSCRIPT_CHUNK_TARGET_CHARS = 1500;
export const TRANSCRIPT_CHUNK_MAX_CHARS = 2200;

/** MiniSearch construction options — must match between build and loadJSON. */
export const MINISEARCH_OPTIONS: { fields: string[]; storeFields: string[] } = {
  fields: ["title", "text"],
  storeFields: [
    "type",
    "title",
    "proposalId",
    "recommenderId",
    "track",
    "transcriptId",
    "chunkStart",
    "chunkEnd",
  ],
};

export const MINISEARCH_QUERY_OPTIONS = {
  prefix: true,
  fuzzy: 0.2,
  boost: { title: 4 },
  weights: { prefix: 0.65, fuzzy: 0.25 },
} as const;

/**
 * Cache key for the persisted index. Bump the leading version (or change any
 * of the encoded parameters) and stale persisted indexes are discarded and
 * rebuilt from the corpus.
 */
export const SEARCH_INDEX_OPTIONS_KEY = [
  // v3 excludes notes written by round members without an active recommender.
  // Invalidate v2 so those documents cannot surface briefly from persistence
  // while the current corpus is still loading.
  "v3",
  `fields=${MINISEARCH_OPTIONS.fields.join(",")}`,
  `stored=${MINISEARCH_OPTIONS.storeFields.join(",")}`,
  `chunk=${TRANSCRIPT_CHUNK_TARGET_CHARS}/${TRANSCRIPT_CHUNK_MAX_CHARS}`,
].join(";");
